import { TrashIcon } from "@heroicons/react/solid";
import React, { useCallback, useRef } from "react";
import { useDrag, useDrop } from 'react-dnd';
import { classNames } from "../../../../../helpers/classNames";
import { LuGripVertical } from "react-icons/lu";
import SearchableColumnSelect from "../../shared/SearchableColumnSelect";

const SmartTableGroupingRow = ({
    allGrouping,
    onGroupingChange,
    columns,
    grouping,
    thisGroupingIndex,
    moveGrouping,
}) => {
    const ref = useRef(null);

    const [{ handlerId }, drop] = useDrop({
        accept: 'grouping',
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = thisGroupingIndex;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveGrouping(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: 'grouping',
        item: () => {
            return { id: grouping.col_id, index: thisGroupingIndex };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0.4 : 1;
    drag(drop(ref));

    const handleUpdateCurrentGrouping = useCallback((updates) => {
        const newGroupingArray = [...allGrouping];
        let finalUpdates = { ...updates };

        // If summaries_only is being enabled, also enable show_summary
        if (updates.summaries_only === true && !grouping.show_summary) {
            finalUpdates.show_summary = true;
        }

        // If show_summary is being disabled, also disable summaries_only
        if (updates.show_summary === false && grouping.summaries_only) {
            finalUpdates.summaries_only = false;
        }

        newGroupingArray[thisGroupingIndex] = { ...grouping, ...finalUpdates };
        onGroupingChange(newGroupingArray);
    }, [allGrouping, thisGroupingIndex, grouping, onGroupingChange]);

    const handleRemoveGrouping = useCallback(() => {
        const newGroupingArray = allGrouping.filter((_, index) => index !== thisGroupingIndex);
        onGroupingChange(newGroupingArray);
    }, [allGrouping, thisGroupingIndex, onGroupingChange]);

    const nonGroupableColumns = ["button", "copy_to_clipboard"];

    return (
        <div
            ref={ref}
            style={{ opacity }}
            data-handler-id={handlerId}
            className={classNames(
                "transition-opacity duration-200",
                isDragging && "cursor-grabbing"
            )}
        >
            <div className="bg-white border border-gray-200 rounded-lg">
                <div className="flex items-stretch divide-x divide-gray-200">
                    {/* Grouping Order Indicator */}
                    <div className="px-2 py-2 flex items-center justify-center">
                        <div className="w-5 h-5 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-semibold">
                            {thisGroupingIndex + 1}
                        </div>
                    </div>

                    {/* Grouping Label */}
                    <div className="px-2 py-2 flex items-center justify-center min-w-[70px] bg-gray-50">
                        <p className="text-xs font-medium text-gray-700">
                            Group by
                        </p>
                    </div>

                    {/* Drag Handle */}
                    <div
                        className="px-2 py-2 cursor-grab hover:bg-gray-50 flex items-center justify-center transition-colors duration-150"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                    >
                        <LuGripVertical className="w-4 h-4 text-gray-400" />
                    </div>

                    {/* Column Selection */}
                    <div className="flex-1 min-w-[200px] overflow-visible">
                        <SearchableColumnSelect
                            columns={columns || []}
                            value={grouping.col_id}
                            onChange={(colId) => handleUpdateCurrentGrouping({ col_id: colId })}
                            excludeTypes={nonGroupableColumns}
                            placeholder="Select column to group by..."
                            className="border-none shadow-none rounded-none focus:ring-0 focus:border-transparent text-sm"
                        />
                    </div>

                    {/* Show Summary Toggle */}
                    <div className="px-2 py-2 min-w-[140px] overflow-visible">
                        <div className="flex items-center h-full">
                            <input
                                type="checkbox"
                                checked={grouping.show_summary || false}
                                onChange={(e) => handleUpdateCurrentGrouping({ show_summary: e.target.checked })}
                                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-xs text-gray-600">
                                Show summary
                            </span>
                        </div>
                    </div>

                    {/* Show Only Summaries Toggle */}
                    <div className="px-2 py-2 min-w-[160px] overflow-visible">
                        <div className="flex items-center h-full">
                            <input
                                type="checkbox"
                                checked={grouping.summaries_only || false}
                                onChange={(e) => handleUpdateCurrentGrouping({ summaries_only: e.target.checked })}
                                disabled={!grouping.show_summary}
                                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded disabled:opacity-50"
                            />
                            <span className={`ml-2 text-xs ${!grouping.show_summary ? 'text-gray-400' : 'text-gray-600'}`}>
                                Summaries only
                            </span>
                        </div>
                    </div>

                    {/* Delete Button */}
                    <div className="flex items-center">
                        <button
                            onClick={handleRemoveGrouping}
                            className="px-3 py-2 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors duration-150 h-full"
                            title="Remove grouping level"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SmartTableGroupingRow; 